import { useTranslation } from "react-i18next"
import { Accordion, Badge, ProgressBar } from "react-bootstrap"
import { useNavigate } from "react-router-dom"
import { useEnrolledByIdData } from "@/queries/queries"
import Loading from "@/components/Loading/Loading"
import NoMessage from "@/components/noMessage/NoMessage"
import { useStore } from "@/zustand/store"
import { CheckCircle, ArrowLeft, BookOpen, Clock } from "lucide-react"
import logo from "@/../assets/images/logo.webp"
import "./CoursePlan.css"

export default function CoursePlan() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const enrolledRoundQuery = useEnrolledByIdData()
  const { branding } = useStore((state) => state)

  if (enrolledRoundQuery.isLoading) return <Loading />
  if (!enrolledRoundQuery.data?.data)
    return <NoMessage name={t("noMessages.data")} />

  const round = enrolledRoundQuery.data.data
  const courseContent = round.round_content || []

  // Calculate progress
  let totalLessons = 0
  let completedLessons = 0

  courseContent.forEach((chapter) => {
    if (chapter.lessons) {
      chapter.lessons.forEach((lesson) => {
        totalLessons++
        if (lesson.is_checked === "1") completedLessons++
      })
    }
  })

  const progressPercentage =
    totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0

  return (
    <div className="course-plan-container">
      <div className="course-plan-top-nav d-flex justify-content-between align-items-center mb-4">
        <button
          className="btn-back d-flex align-items-center gap-2"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={18} />
          <span>{t("common.back")}</span>
        </button>
        <div className="logo-container">
          <img
            src={branding?.siteLogo || logo}
            alt="Logo"
            className="brand-logo"
          />
        </div>
      </div>

      <div className="course-plan-header mb-5">
        <div className="d-flex justify-content-between align-items-end mb-3">
          <div>
            <Badge bg="primary" className="mb-2 px-3 py-2 rounded-pill">
              {round.course?.title || t("common.course")}
            </Badge>
            <h1 className="course-plan-title display-6 fw-bold">
              {round.name}
            </h1>
          </div>
          <div className="text-end">
            <span className="h4 fw-bold text-primary mb-0">
              {progressPercentage}%
            </span>
            <p className="text-muted small mb-0">
              {t("enrolledCoursesPage.rounds.attendanceRate")}
            </p>
          </div>
        </div>
        <ProgressBar
          now={progressPercentage}
          className="course-progress-bar rounded-pill"
          style={{ height: "10px" }}
        />
        <div className="d-flex gap-4 mt-3 text-muted small">
          <span className="d-flex align-items-center gap-1">
            <BookOpen size={14} /> {totalLessons}{" "}
            {t("enrolledCoursesPage.rounds.lecturesCount")}
          </span>
          <span className="d-flex align-items-center gap-1">
            <Clock size={14} /> {completedLessons} {t("common.completed")}
          </span>
        </div>
      </div>

      <div className="plan-content">
        <h4 className="mb-4 fw-bold">
          {t("enrolledCoursesModalsTitles.mindMap")}
        </h4>
        <Accordion defaultActiveKey="0" className="professional-accordion">
          {courseContent.map((chapter, chapterIndex) => (
            <Accordion.Item
              eventKey={String(chapterIndex)}
              key={chapter.id}
              className="mb-3 border rounded-4 overflow-hidden shadow-sm"
            >
              <Accordion.Header>
                <div className="d-flex align-items-center w-100 gap-3 py-1">
                  <div className="chapter-badge">{chapterIndex + 1}</div>
                  <div className="flex-grow-1">
                    <span className="fw-bold d-block">
                      {chapter.name ||
                        `${t("common.chapter")} ${chapterIndex + 1}`}
                    </span>
                    <span className="text-muted smaller">
                      {chapter.lessons?.length || 0}{" "}
                      {t("enrolledCoursesPage.rounds.lecturesCount")}
                    </span>
                  </div>
                  {chapter.is_checked === "1" && (
                    <Badge
                      bg="success"
                      pill
                      className="me-3 px-3 py-2 d-flex align-items-center gap-1"
                    >
                      <CheckCircle size={14} /> {t("common.completed")}
                    </Badge>
                  )}
                </div>
              </Accordion.Header>
              <Accordion.Body className="bg-white p-0">
                {chapter.lessons && chapter.lessons.length > 0 ? (
                  <div className="lessons-list">
                    {chapter.lessons.map((lesson, lessonIndex) => (
                      <div
                        key={lesson.id}
                        className="lesson-item d-flex align-items-start py-3 px-4 border-bottom last-child-border-0"
                      >
                        <div className="lesson-indicator mt-1">
                          {lesson.is_checked === "1" ? (
                            <CheckCircle className="text-success" size={20} />
                          ) : (
                            <div className="lesson-circle"></div>
                          )}
                        </div>
                        <div className="flex-grow-1 ps-3">
                          <h6
                            className={`mb-1 lesson-title ${lesson.is_checked === "1" ? "text-success" : ""}`}
                          >
                            {lesson.title ||
                              `${t("common.lesson")} ${lessonIndex + 1}`}
                          </h6>
                          {lesson.topics && lesson.topics.length > 0 && (
                            <div className="topics-grid mt-2">
                              {lesson.topics.map((topic) => (
                                <div key={topic.id} className="topic-tag">
                                  <span>{topic.title}</span>
                                  {topic.is_checked === "1" && (
                                    <CheckCircle
                                      size={12}
                                      className="ms-1 text-success"
                                    />
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <p className="text-muted mb-0">{t("common.noData")}</p>
                  </div>
                )}
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </div>
    </div>
  )
}
